import React, { useState } from 'react';

import { dashboardCompanyApis } from '@apis/next/companyApis';
import { TextInput } from '@components/admin/Forms';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogContent,
  ActionableDialogContentDivider,
  ActionableDialogTitle,
} from '@components/Dialog';
import { ButtonInput } from '@components/Form';
import { Pagination } from '@components/Pagination';
import { QueryContainer } from '@components/QueryContainer';
import { ContactDisplay } from '@components/utilities/BookingInquiry/ContactDisplay';
import { companyStrings } from '@data/company';
import { useMemberList } from '@hooks/queries/dashboard/useMemberList';
import CheckIcon from '@material-ui/icons/Check';
import { OwnerCompanySearchForm, ownerCompanySearchForm } from '@models/Company';
import { type ContactBase } from '@models/Contact';
import type { DashboardMemberQuery, Member } from '@models/Member';
import { AdminUser, CompanyStaff, UserHelper } from '@models/User';
import { BookingCreateCompanyInfo } from '@pages/admin/booking/BookingCreate';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createValidate, formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { Form, Formik } from 'formik';

const strings = createStrings({
  ja: {
    chooseOwner: 'Ownerを選択してください',
    chooseClientStaff: '客側担当者を選択してください',
    phoneNumber: '電話番号',
    email: 'メールアドレス',
    mobilePhoneNumber: '携帯電話番号',
    fcsCode: '会員番号(FCS CODE)',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  mainContainer: {
    ...common.flexColumn,
    rowGap: '1rem',
    paddingTop: '1rem',
    paddingBottom: '1rem',
  },
  dialog: {
    maxWidth: '960px !important',
  },
  filterContainer: {
    ...common.flexInline,
    alignItems: 'flex-end',
    columnGap: '1.5rem',
    flexWrap: 'wrap',
    [theme.breakpoints.down('xs')]: {
      rowGap: '1.5rem',
      justifyContent: 'center',
    },
  },
  filterRoot: {
    [theme.breakpoints.down('sm')]: {
      width: '30%',
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  label: {
    minWidth: 'min-content',
    width: 'initial',
  },
  noContent: {
    ...common.textGray500,
    ...common.smallerText,
    ...common.textAlignCenter,
    paddingTop: 40,
    height: 360,
  },
  listContainer: {
    ...common.flexColumn,
    overflow: 'auto',
    height: 360,
    rowGap: 10,
    [theme.breakpoints.down('xs')]: {
      height: '23vh',
    },
  },
  companyItemRow: {
    ...common.flexInline,
  },
  companyItemColumn: {
    minWidth: 200,
  },
  companyItemSelectedDot: {
    height: 16,
    width: 16,
    flex: '0 0 16px',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: '50%',
    '$selected &': {
      backgroundColor: theme.palette.primary.main,
      border: `1px solid ${theme.palette.background.paper}`,
    },
  },
  itemSelectButton: {
    ...common.flexCenter,
    ...common.textPrimary,
    ...common.bigText,
    width: 111,
    height: 38,
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.background.paper,
    '$selected &': {
      border: `1px solid ${theme.palette.common.white}`,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
  },
  itemWrapper: {
    ...common.flexInline,
    columnGap: 1,
  },
  itemContainer: {
    ...common.flexInline,
    ...common.itemsCenter,
    ...common.justifyBetween,
    flex: 1,
    padding: '20px 20px',
    backgroundColor: theme.palette.grey[100],
    [theme.breakpoints.down('xs')]: {
      width: '297px',
      padding: '1rem',
    },
  },
  itemIndicator: {
    backgroundColor: theme.palette.grey[100],
    width: 12,
    flex: '0 0 12px',
    borderRadius: 2,
    '$selected &': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  buttonContainer: {
    ...common.flexInline,
    ...common.justifyBetween,
    padding: '1rem',
  },
  staffInfoContainer: {
    ...common.middleText,
    marginTop: '0.5rem',
    display: 'grid',
    gridTemplateColumns: '8rem 1fr',
  },
  staffInfoRow: {
    display: 'contents',
  },
  selected: {},
}));

interface CompanyItemProps {
  selected: boolean;
  contact: ContactBase | undefined | null;
  onSelect: () => void;
  onDeselect: () => void;
}

const CompanyItem = React.memo(
  React.forwardRef<HTMLDivElement, CompanyItemProps>((props, ref) => {
    const { selected, contact, onSelect, onDeselect } = props;

    const classes = {
      ...useCommonStyles(),
      ...useStyles(),
    };

    return (
      <div
        data-testid="CompanyItem"
        ref={ref}
        onClick={() => (selected ? onDeselect() : onSelect())}
        className={clsx(classes.itemWrapper, classes.baseButton, selected && classes.selected)}
      >
        <div className={classes.itemIndicator} />

        {contact && (
          <div className={classes.itemContainer}>
            <ContactDisplay contact={contact} variant="big" />
            <div data-testid="btn-company-select" className={classes.itemSelectButton}>
              {!selected && <span>{formStrings.choose}</span>}
              {selected && (
                <>
                  <CheckIcon /> <span>{formStrings.chosen} </span>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }),
);

interface CompanyListProps {
  companies: Member[];
  selectedId: number;
  onSelectId: (id: number) => void;
}

const CompanyList: React.FC<CompanyListProps> = (props) => {
  const { companies, selectedId, onSelectId } = props;
  const classes = useStyles();
  return (
    <div data-testid="CompanyList" className={classes.listContainer}>
      {companies.map((company) => (
        <CompanyItem
          key={company.id}
          contact={company.contact}
          selected={company.id === selectedId}
          onSelect={() => {
            onSelectId(company.id ?? 0);
          }}
          onDeselect={() => onSelectId(0)}
        />
      ))}
    </div>
  );
};

interface StaffItemProps {
  staff: Omit<CompanyStaff, 'email_verified'>;
  selected: boolean;
  onSelect: () => void;
  onDeselect: () => void;
}

const StaffItem = React.memo(
  React.forwardRef<HTMLDivElement, StaffItemProps>((props, ref) => {
    const { staff, selected, onSelect, onDeselect } = props;
    const classes = {
      ...useCommonStyles(),
      ...useStyles(),
    };

    return (
      <div
        data-testid="StaffItem"
        ref={ref}
        onClick={() => (selected ? onDeselect() : onSelect())}
        className={clsx(classes.itemWrapper, classes.baseButton, selected && classes.selected)}
      >
        <div className={classes.itemIndicator} />
        <div className={classes.itemContainer}>
          <div>
            <div>{`${staff.last_name} ${staff.first_name}`}</div>
            <div className={classes.staffInfoContainer}>
              <div className={classes.staffInfoRow}>
                <div>{strings.phoneNumber}</div>
                <div>{staff.landline}</div>
              </div>
              <div className={classes.staffInfoRow}>
                <div>{strings.mobilePhoneNumber}</div>
                <div>{staff.cellphone}</div>
              </div>
              <div className={classes.staffInfoRow}>
                <div>{strings.email}</div>
                <div>{staff.email}</div>
              </div>
            </div>
          </div>
          <div data-testid="btn-staff-select" className={classes.itemSelectButton}>
            {!selected && <span>{formStrings.choose}</span>}
            {selected && (
              <>
                <CheckIcon /> <span>{formStrings.chosen} </span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }),
);

interface StaffListProps {
  staffs: Omit<CompanyStaff, 'email_verified'>[];
  selectedId: number;
  onSelectId: (id: number) => void;
}

const StaffList: React.FC<StaffListProps> = (props) => {
  const { staffs, selectedId, onSelectId } = props;
  const classes = useStyles();
  return (
    <div data-testid="CompanyList" className={classes.listContainer}>
      {staffs.map((staff) => (
        <StaffItem
          key={staff.id}
          staff={staff}
          selected={staff.id === selectedId}
          onSelect={() => onSelectId(staff.id)}
          onDeselect={() => onSelectId(0)}
        />
      ))}
    </div>
  );
};
interface CompanyFilterProps {
  query: DashboardMemberQuery;
  onChangeQuery?: (query: DashboardMemberQuery) => void;
}

const CompanyFilter: React.FC<CompanyFilterProps> = (props) => {
  const { query, onChangeQuery } = props;
  const classes = useStyles();

  return (
    <Formik<OwnerCompanySearchForm>
      initialValues={{ company_name: query.company_name ?? '', member_no: query.member_no ?? '' }}
      validate={createValidate(ownerCompanySearchForm)}
      onSubmit={(values) => {
        onChangeQuery?.({
          ...values,
        });
      }}
    >
      {({ resetForm, handleSubmit }) => (
        <Form className={classes.filterContainer}>
          <TextInput
            name="company_name"
            label={companyStrings.company_name}
            formClasses={{
              root: classes.filterRoot,
              label: classes.label,
            }}
          />

          <TextInput
            name="member_no"
            label={strings.fcsCode}
            formClasses={{
              root: classes.filterRoot,
              label: classes.label,
            }}
          />

          <ButtonInput text={formStrings.search} color="primary" size="small" />

          <ButtonInput
            text={formStrings.reset}
            color="primary"
            type="button"
            size="small"
            onClick={() => {
              resetForm({
                values: {
                  company_name: '',
                  member_no: '',
                  page: 1,
                },
              });
              handleSubmit();
            }}
          />
        </Form>
      )}
    </Formik>
  );
};

const MEMBER_PAGE_SIZE = 10;

interface BookingInquiryOwnerChooseDialogProps {
  open: boolean;
  onClose: () => void;
  setOwnerId: (id: number) => void;
  setCompanyInfo: (compnayInfo: BookingCreateCompanyInfo) => void;
  setOwnerName: (name: string) => void;
}

export const BookingInquiryOwnerChooseDialog: React.FC<BookingInquiryOwnerChooseDialogProps> = (
  props,
) => {
  const { open, onClose, setOwnerId, setCompanyInfo, setOwnerName } = props;
  const classes = { ...useCommonStyles(), ...useStyles() };
  const [step, setStep] = useState(0);
  const [selectedCompanyId, setSelectedCompanyId] = useState(0);
  const [selectedStaffId, setSelectedStaffId] = useState(0);
  const [queryParams, setQueryParams] = useState<DashboardMemberQuery>({
    company_name: '',
    member_no: '',
    page: 1,
    page_size: MEMBER_PAGE_SIZE,
  });

  const companies = useMemberList({ ...queryParams });
  const companyStaffsQuery = dashboardCompanyApis.useGet(
    '/dashboard/company/:id/staff',
    {
      params: {
        id: selectedCompanyId,
      },
    },
    {
      enabled: !!selectedCompanyId,
    },
  );

  const companiesResult = companies.data?.results ?? [];

  const selectedCompany = companiesResult.find((company) => company.id === selectedCompanyId);

  const companyUser = selectedCompany?.user;
  const clientStaffList = [companyUser!, ...(companyStaffsQuery.data ?? [])];

  const selectedStaff = clientStaffList.find((staff) => staff?.id === selectedStaffId);

  const renderNoResult = () => {
    return <div className={classes.noContent}>{formStrings.noResult(companyStrings.company)}</div>;
  };

  return (
    <ActionableDialog
      open={open}
      onClose={onClose}
      classes={{
        paper: classes.dialog,
      }}
      muiDialogProps={{
        disablePortal: false,
      }}
    >
      {(step === 0 || selectedCompanyId === 0) && (
        <>
          <ActionableDialogTitle>{strings.chooseOwner}</ActionableDialogTitle>
          <QueryContainer queries={[companies]}>
            <ActionableDialogContent>
              <div className={classes.mainContainer}>
                <CompanyFilter
                  query={queryParams}
                  onChangeQuery={(query) =>
                    setQueryParams((queryParams) => ({ ...queryParams, ...query, page: 1 }))
                  }
                />
                {companiesResult.length > 0 && (
                  <CompanyList
                    companies={companiesResult}
                    selectedId={selectedCompanyId}
                    onSelectId={setSelectedCompanyId}
                  />
                )}
                {companiesResult.length === 0 && renderNoResult()}
                <div className={clsx(classes.flexInline, classes.justifyEnd)}>
                  <Pagination
                    page={queryParams.page! - 1}
                    count={Math.ceil((companies.data?.count ?? 0) / MEMBER_PAGE_SIZE)}
                    onChange={(page) => setQueryParams({ ...queryParams, page: page + 1 })}
                  />
                </div>
              </div>
            </ActionableDialogContent>
            <ActionableDialogContentDivider />
            <div style={{ padding: '1rem 24px' }}>
              <Button
                color="primary"
                text={formStrings.next}
                size="small"
                data-testid="close-contact-filter"
                onClick={() => {
                  setStep(1);
                }}
                style={{ width: '100%' }}
                disabled={selectedCompanyId === 0}
              />
            </div>
          </QueryContainer>
        </>
      )}
      {step === 1 && selectedCompanyId !== 0 && (
        <>
          <ActionableDialogTitle>{strings.chooseClientStaff}</ActionableDialogTitle>
          <QueryContainer queries={[companyStaffsQuery]}>
            <ActionableDialogContent>
              <div className={classes.mainContainer}>
                <StaffList
                  staffs={clientStaffList}
                  selectedId={selectedStaffId}
                  onSelectId={(id) => setSelectedStaffId(id)}
                />
              </div>
            </ActionableDialogContent>
            <ActionableDialogContentDivider />
            <div className={classes.buttonContainer}>
              <Button
                color="grey"
                text={formStrings.prev}
                size="small"
                data-testid="close-contact-filter"
                onClick={() => {
                  setStep(0);
                }}
                style={{ width: '48%' }}
              />
              <Button
                color="primary"
                text={formStrings.confirm}
                size="small"
                data-testid="close-contact-filter"
                onClick={() => {
                  setOwnerId(selectedStaffId);
                  setCompanyInfo({
                    id: selectedCompany?.user?.id,
                    companyName: selectedCompany?.company_name,
                    memberNo: selectedCompany?.member_no,
                    country: selectedCompany?.country || 'jp',
                  });
                  setOwnerName(
                    selectedStaff ? new UserHelper(selectedStaff as AdminUser).fullname : '-',
                  );
                  onClose();
                }}
                style={{ width: '48%' }}
                disabled={selectedStaffId === 0}
              />
            </div>
          </QueryContainer>
        </>
      )}
    </ActionableDialog>
  );
};
